import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Chip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Stack,
  Divider
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  VideoCall as MeetingIcon,
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Celebration as HolidayIcon,
  Person as PersonIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add token to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Color options for personal marks
const COLOR_OPTIONS = [
  { value: '#f59e0b', label: 'Amber', bg: 'rgba(245, 158, 11, 0.1)' },
  { value: '#ef4444', label: 'Red', bg: 'rgba(239, 68, 68, 0.1)' },
  { value: '#3b82f6', label: 'Blue', bg: 'rgba(59, 130, 246, 0.1)' },
  { value: '#10b981', label: 'Green', bg: 'rgba(16, 185, 129, 0.1)' },
  { value: '#8b5cf6', label: 'Purple', bg: 'rgba(139, 92, 246, 0.1)' },
  { value: '#ec4899', label: 'Pink', bg: 'rgba(236, 72, 153, 0.1)' },
  { value: '#f97316', label: 'Orange', bg: 'rgba(249, 115, 22, 0.1)' },
  { value: '#06b6d4', label: 'Cyan', bg: 'rgba(6, 182, 212, 0.1)' }
];

export default function PersonalizedCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarItems, setCalendarItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date(),
    time: null,
    color: '#f59e0b',
    type: 'personal'
  });
  const [formErrors, setFormErrors] = useState({});

  // Get current user info
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        
        try {
          const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
          if (cmpRes.data && cmpRes.data.data) {
            setCompanyConfig(cmpRes.data.data);
          }
        } catch (e) {}
        
        try {
          const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });

          setCurrentUser(response.data);
          return response.data;
        } catch (err) {
          // Fallback to profile endpoint
          const response = await apiClient.get(`/api/user/profile`, {
            params: { company_id }
          });
          setCurrentUser(response.data);
          return response.data;
        }
      } catch (err) {
        //console.error('Error fetching user:', err);
        return null;
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch all calendar data
  useEffect(() => {
    const fetchCalendarData = async () => {
      if (!currentUser) return;

      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        const employeeId = currentUser.employee_id || currentUser.employeeId;

        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();

        // Try new combined endpoint first
        try {
          const response = await apiClient.get('/api/calendar', {
            params: {
              employee_id: employeeId,
              month,
              year
            }
          });
          setCalendarItems(response.data.data || []);
        } catch (err) {
          // Fallback to individual endpoints
          await fetchIndividualData(employeeId, company_id, token, month, year);
        }

      } catch (err) {
        //console.error('Error fetching calendar data:', err);
        setError('Failed to load calendar data');
      } finally {
        setLoading(false);
      }
    };

    const fetchIndividualData = async (employeeId, company_id, token, month, year) => {
      try {
        // Fetch events
        const eventsRes = await apiClient.get(`/api/em/${employeeId}`);
        
        // Fetch meetings
        const meetingsRes = await apiClient.get('/api/meetings');
        
        // Fetch holidays
        const holidaysRes = await apiClient.get('/api/calendar/holidays', {
          params: { year, company_id }
        });
        
        // Fetch personal marks
        const marksRes = await apiClient.get('/api/personal-marks', {
          params: {
            employee_id: employeeId,
            month,
            year
          }
        });

        const events = eventsRes.data?.data || [];
        const meetings = meetingsRes.data?.meetings || [];
        const holidays = holidaysRes.data?.data || [];
        const personalMarks = marksRes.data?.data || [];

        // Format and combine
        const formattedEvents = events.map(e => ({
          id: `event-${e.id}`,
          title: e.title,
          date: e.date,
          time: e.time,
          type: 'event',
          color: '#1976d2',
          data: e
        }));

        const formattedMeetings = meetings.map(m => ({
          id: `meeting-${m.id}`,
          title: m.title,
          date: m.date,
          time: m.time,
          type: 'meeting',
          color: '#9c27b0',
          data: m
        }));

        setCalendarItems([
          ...formattedEvents,
          ...formattedMeetings,
          ...holidays,
          ...personalMarks
        ]);
      } catch (err) {
        //console.error('Error in individual fetch:', err);
      }
    };

    if (currentUser) {
      fetchCalendarData();
    }
  }, [currentUser, currentDate]);

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format date to YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return null;
    
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Handle date cell click
  const handleDateCellClick = (day) => {
    // Only allow adding on current month days
    if (day.isCurrentMonth) {
      setFormData({
        ...formData,
        date: day.date,
        title: '',
        description: '',
        time: null,
        color: '#f59e0b'
      });
      setFormErrors({});
      setOpenAddDialog(true);
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      const dateStr = formatDate(current);
      const dayItems = calendarItems.filter(item => item.date === dateStr);
      
      days.push({
        date: new Date(current),
        dateStr,
        isCurrentMonth: current.getMonth() === month,
        isToday: current.toDateString() === new Date().toDateString(),
        items: dayItems
      });
      
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();

  // Handle add personal mark
  const handleAddMark = async () => {
    // Validate form
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.date) errors.date = 'Date is required';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const employeeId = currentUser.employee_id || currentUser.employeeId || currentUser.id;

      const response = await apiClient.post('/api/personal-marks', {
        employee_id: employeeId,
        company_id,
        title: formData.title,
        description: formData.description,
        date: formatDate(formData.date),
        time: formData.time ? formData.time.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }) : null,
        color: formData.color,
        type: formData.type
      });

      // Add new item to calendar
      setCalendarItems(prev => [...prev, response.data.data]);
      
      // Reset form and close dialog
      setFormData({
        title: '',
        description: '',
        date: new Date(),
        time: null,
        color: '#f59e0b',
        type: 'personal'
      });
      setFormErrors({});
      setOpenAddDialog(false);

    } catch (err) {
      //console.error('Error adding mark:', err);
      setError(err.response?.data?.message || 'Failed to add mark');
    }
  };

  // Handle delete personal mark
  const handleDeleteMark = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const actualId = id.replace('personal-', '');
      await apiClient.delete(`/api/personal-marks/${actualId}`);
      
      setCalendarItems(prev => prev.filter(item => item.id !== id));
      setOpenViewDialog(false);
      setSelectedItem(null);
      
    } catch (err) {
      //console.error('Error deleting mark:', err);
      setError(err.response?.data?.message || 'Failed to delete mark');
    }
  };

  // Handle toggle completion
  const handleToggleComplete = async (id) => {
    try {
      const actualId = id.replace('personal-', '');
      const response = await apiClient.patch(`/api/personal-marks/${actualId}/toggle`);
      
      // Update item in state
      setCalendarItems(prev => prev.map(item => {
        if (item.id === id) {
          return { ...item, isCompleted: response.data.data.isCompleted };
        }
        return item;
      }));

      // Update selected item if dialog is open
      if (selectedItem && selectedItem.id === id) {
        setSelectedItem({ ...selectedItem, isCompleted: response.data.data.isCompleted });
      }
      
    } catch (err) {
      //console.error('Error toggling completion:', err);
    }
  };

  // Event tooltip content
  const EventTooltipContent = ({ item }) => (
    <Box sx={{ p: 1, maxWidth: 250 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {item.type === 'event' && <EventIcon sx={{ color: item.color, fontSize: 16 }} />}
        {item.type === 'meeting' && <MeetingIcon sx={{ color: item.color, fontSize: 16 }} />}
        {item.type === 'holiday' && <HolidayIcon sx={{ color: item.color, fontSize: 16 }} />}
        {item.type === 'personal' && <PersonIcon sx={{ color: item.color, fontSize: 16 }} />}
        
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
          {item.title}
        </Typography>
        
        <Chip 
          label={item.type} 
          size="small" 
          sx={{ 
            height: 20, 
            fontSize: '0.6rem',
            bgcolor: alpha(item.color, 0.1),
            color: item.color,
            fontWeight: 600
          }}
        />
      </Box>
      
      <Typography variant="caption" display="block" color="text.secondary">
        {new Date(item.date).toLocaleDateString('en-US', { 
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: companyConfig?.timezone || undefined
        })}
      </Typography>
      
      {item.time && (
        <Typography variant="caption" display="block" color="text.secondary">
          🕒 {item.time}
        </Typography>
      )}
      
      {item.description && (
        <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
          {item.description}
        </Typography>
      )}
      
      {item.type === 'personal' && (
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={item.isCompleted || false}
                onChange={() => handleToggleComplete(item.id)}
                color="success"
              />
            }
            label={
              <Typography variant="caption">
                {item.isCompleted ? 'Completed' : 'Mark as complete'}
              </Typography>
            }
          />
        </Box>
      )}
      
      {item.type === 'meeting' && item.link && (
        <Button 
          size="small" 
          variant="outlined" 
          sx={{ mt: 1 }}
          onClick={() => window.open(item.link, '_blank')}
        >
          Join Meeting
        </Button>
      )}
      
      {item.type === 'holiday' && item.isOptional && (
        <Chip 
          label="Optional Holiday" 
          size="small" 
          sx={{ mt: 1, fontSize: '0.6rem' }}
          variant="outlined"
        />
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarIcon sx={{ color: '#1976d2', fontSize: 24 }} />
          <Typography variant="h6" fontWeight="bold">
            My Calendar
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={goToPreviousMonth}>
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="body1" sx={{ minWidth: 140, textAlign: 'center', fontWeight: 600 }}>
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: companyConfig?.timezone || undefined })}
            </Typography>
            
            <IconButton size="small" onClick={goToNextMonth}>
              <ChevronRight />
            </IconButton>
          </Box>
          
          <Button
            startIcon={<TodayIcon />}
            onClick={goToToday}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2, textTransform: 'none', ml: 1 }}
          >
            Today
          </Button>
        </Box>
      </Box>

      {/* Legend */}
      <Paper sx={{ mb: 2, p: 1, borderRadius: 2, bgcolor: '#f8fafc' }}>
        <Stack direction="row" spacing={2} justifyContent="center" divider={<Divider orientation="vertical" flexItem />}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box width={12} height={12} borderRadius={1} bgcolor="#1976d2" />
            <Typography variant="caption">Events</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box width={12} height={12} borderRadius={1} bgcolor="#9c27b0" />
            <Typography variant="caption">Meetings</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box width={12} height={12} borderRadius={1} bgcolor="#10b981" />
            <Typography variant="caption">Holidays</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box width={12} height={12} borderRadius={1} bgcolor="#f59e0b" />
            <Typography variant="caption">Personal</Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ 
            minHeight: '36px',
            '& .MuiTab-root': { 
              minHeight: '36px', 
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none'
            }
          }}
        >
          <Tab label="Month View" />
          <Tab label="List View" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Month View */}
      {activeTab === 0 && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Weekday headers */}
          <Grid container spacing={0.5} sx={{ mb: 1 }}>
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
              <Grid item xs key={day} sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar grid */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Grid container spacing={0}>
              {calendarDays.map((day, index) => (
                <Grid item xs={12/7} key={index}>
                  <Paper
                    elevation={0}
                    onClick={() => handleDateCellClick(day)}
                    sx={{
                      p: 0.5,
                      minHeight: 80,
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: day.isCurrentMonth ? 
                        (day.isToday ? alpha('#1976d2', 0.05) : '#fff') : 
                        alpha('#000', 0.02),
                      border: '1px solid',
                      borderColor: 'divider',
                      opacity: day.isCurrentMonth ? 1 : 0.5,
                      cursor: day.isCurrentMonth ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      '&:hover': {
                        backgroundColor: day.isCurrentMonth ? 
                          alpha('#1976d2', 0.08) : 
                          alpha('#000', 0.03),
                        transform: day.isCurrentMonth ? 'scale(1.02)' : 'none',
                        zIndex: 1,
                        boxShadow: day.isCurrentMonth ? 2 : 0
                      }
                    }}
                  >
                    {/* Date number */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: day.isToday ? 700 : 400,
                        color: day.isToday ? '#1976d2' : (day.isCurrentMonth ? 'text.primary' : 'text.disabled'),
                        fontSize: '0.8rem',
                        p: 0.5,
                        alignSelf: 'flex-start'
                      }}
                    >
                      {day.date.getDate()}
                    </Typography>

                    {/* Add indicator on hover */}
                    {day.isCurrentMonth && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                          '.MuiPaper-root:hover &': {
                            opacity: 0.6
                          },
                          '&:hover': {
                            opacity: 1,
                          }
                        }}
                      >
                        <AddIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                      </Box>
                    )}

                    {/* Event Icons */}
                    <Box sx={{ 
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.3,
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: 'auto',
                      mb: 0.5,
                      px: 0.5
                    }}>
                      {day.items.slice(0, 3).map((item) => (
                        <Tooltip 
                          key={item.id} 
                          title={<EventTooltipContent item={item} />}
                          arrow
                          placement="top"
                          enterDelay={300}
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent date cell click
                              if (item.type === 'personal') {
                                setSelectedItem(item);
                                setOpenViewDialog(true);
                              }
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: '4px',
                              backgroundColor: alpha(item.color, 0.9),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: item.type === 'personal' ? 'pointer' : 'default',
                              transition: 'all 0.2s ease',
                              boxShadow: `0 2px 4px ${alpha(item.color, 0.3)}`,
                              '&:hover': {
                                transform: item.type === 'personal' ? 'scale(1.2)' : 'none',
                                backgroundColor: item.color
                              },
                              ...(item.type === 'personal' && item.isCompleted && {
                                opacity: 0.6,
                                textDecoration: 'line-through'
                              })
                            }}
                          >
                            {item.type === 'event' && <EventIcon sx={{ color: '#fff', fontSize: 14 }} />}
                            {item.type === 'meeting' && <MeetingIcon sx={{ color: '#fff', fontSize: 14 }} />}
                            {item.type === 'holiday' && <HolidayIcon sx={{ color: '#fff', fontSize: 14 }} />}
                            {item.type === 'personal' && (
                              item.isCompleted ? 
                                <CheckCircleIcon sx={{ color: '#fff', fontSize: 14 }} /> :
                                <PersonIcon sx={{ color: '#fff', fontSize: 14 }} />
                            )}
                          </Box>
                        </Tooltip>
                      ))}
                      
                      {day.items.length > 3 && (
                        <Tooltip 
                          title={`${day.items.length - 3} more items`}
                          arrow
                        >
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: '4px',
                              backgroundColor: '#757575',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'default',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              color: '#fff'
                            }}
                          >
                            +{day.items.length - 3}
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}

      {/* List View */}
      {activeTab === 1 && (
        <Box sx={{ 
          height: '600px',
          overflow: 'auto',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
              background: '#666',
            },
          },
        }}>
          {calendarItems
            .sort((a, b) => {
              const dateA = new Date(a.date + (a.time ? 'T' + a.time : ''));
              const dateB = new Date(b.date + (b.time ? 'T' + b.time : ''));
              return dateA - dateB;
            })
            .map(item => (
              <Tooltip 
                key={item.id} 
                title={<EventTooltipContent item={item} />}
                arrow
                placement="left"
              >
                <Card 
                  sx={{ 
                    mb: 1, 
                    borderLeft: `4px solid ${item.color}`,
                    cursor: item.type === 'personal' ? 'pointer' : 'default',
                    transition: 'all 0.2s ease',
                    opacity: item.type === 'personal' && item.isCompleted ? 0.6 : 1,
                    '&:hover': {
                      transform: item.type === 'personal' ? 'translateX(4px)' : 'none',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => {
                    if (item.type === 'personal') {
                      setSelectedItem(item);
                      setOpenViewDialog(true);
                    }
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Box sx={{ color: item.color, display: 'flex', alignItems: 'center', mt: 0.25 }}>
                        {item.type === 'event' && <EventIcon fontSize="small" />}
                        {item.type === 'meeting' && <MeetingIcon fontSize="small" />}
                        {item.type === 'holiday' && <HolidayIcon fontSize="small" />}
                        {item.type === 'personal' && (
                          item.isCompleted ? 
                            <CheckCircleIcon fontSize="small" /> :
                            <PersonIcon fontSize="small" />
                        )}
                      </Box>
                      
                      <Box flexGrow={1}>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {item.title}
                          {item.type === 'personal' && item.isCompleted && ' ✓'}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric',
                              timeZone: companyConfig?.timezone || undefined
                            })}
                          </Typography>
                          
                          {item.time && (
                            <>
                              <Typography variant="caption" color="text.secondary">•</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.time}
                              </Typography>
                            </>
                          )}
                          
                          <Chip 
                            label={item.type} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.6rem',
                              bgcolor: alpha(item.color, 0.1),
                              color: item.color,
                              fontWeight: 600
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Tooltip>
            ))}
          
          {calendarItems.length === 0 && (
            <Box textAlign="center" py={4} sx={{ bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
              <CalendarIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No items for this month
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Click on any date in Month View to add a personal mark
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {calendarItems.length} item{calendarItems.length !== 1 ? 's' : ''} this month
        </Typography>
        
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AddIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
          Click any date to add
        </Typography>
      </Box>

      {/* Add Personal Mark Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon sx={{ color: '#f59e0b' }} />
            <Typography variant="h6">Add Personal Mark for {formData.date ? new Date(formData.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: companyConfig?.timezone || undefined }) : ''}</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title *"
              fullWidth
              size="small"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              error={!!formErrors.title}
              helperText={formErrors.title}
              autoFocus
            />
            
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date *"
                value={formData.date}
                onChange={(newDate) => setFormData({ ...formData, date: newDate })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!formErrors.date,
                    helperText: formErrors.date
                  }
                }}
              />
              
              <TimePicker
                label="Time (optional)"
                value={formData.time}
                onChange={(newTime) => setFormData({ ...formData, time: newTime })}
                ampm={false}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
            
            <FormControl fullWidth size="small">
              <InputLabel>Color</InputLabel>
              <Select
                value={formData.color}
                label="Color"
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              >
                {COLOR_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box width={20} height={20} borderRadius={1} bgcolor={option.value} />
                      <Typography>{option.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddMark}
            sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#f97316' } }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* View/Edit Personal Mark Dialog */}
      <Dialog 
        open={openViewDialog} 
        onClose={() => setOpenViewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedItem && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ color: selectedItem.color }} />
                  <Typography variant="h6">{selectedItem.title}</Typography>
                </Box>
                <IconButton size="small" onClick={() => setOpenViewDialog(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent dividers>
              <Stack spacing={2}>
                {selectedItem.description && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2">{selectedItem.description}</Typography>
                  </Box>
                )}
                
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Date & Time
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedItem.date).toLocaleDateString('en-US', { 
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: companyConfig?.timezone || undefined
                    })}
                    {selectedItem.time && ` at ${selectedItem.time}`}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" gap={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={selectedItem.isCompleted || false}
                        onChange={() => handleToggleComplete(selectedItem.id)}
                        color="success"
                      />
                    }
                    label="Mark as completed"
                  />
                  
                  <Chip 
                    label={selectedItem.type}
                    size="small"
                    sx={{ 
                      bgcolor: alpha(selectedItem.color, 0.1),
                      color: selectedItem.color
                    }}
                  />
                </Box>
              </Stack>
            </DialogContent>
            
            <DialogActions>
              <Button 
                color="error" 
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteMark(selectedItem.id)}
              >
                Delete
              </Button>
              <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}